import { useEffect, useRef, useState } from "react";
import type React from "react";
import type { ProceduralAudio } from "../../audio/procedural-audio";
import { MatchEngine } from "../../game/match";
import type { MatchMode, MatchSnapshot } from "../../game/types";
import { InputController } from "../../input/input-controller";
import type { EndlessCheckpoint } from "../../progress/match-persistence";
import type { CosmeticId, ProgressSettings } from "../../progress/schema";
import { CanvasRenderer } from "../../render/canvas-renderer";
import { FramePacer } from "../../render/frame-pacer";
import { interpolateMatchSnapshot } from "../../render/interpolation";
import type { AssistEnableResult, MatchResult } from "../../app/types";
import { resultFromEngine } from "../../app/match-result";
import { Leaderboard } from "./Leaderboard";
import { Dialog } from "../Dialog";
import { Icon } from "../Icon";

export function MatchView({
  mode,
  settings,
  cosmetic,
  audio,
  assistAllowed,
  showTutorial,
  resultVisible,
  onEnableAssist,
  onCheckpoint,
  onFinish,
  onExit,
}: {
  readonly mode: MatchMode;
  readonly settings: ProgressSettings;
  readonly cosmetic: CosmeticId;
  readonly audio: ProceduralAudio;
  readonly assistAllowed: boolean;
  readonly showTutorial: boolean;
  readonly resultVisible: boolean;
  readonly onEnableAssist: () => Promise<AssistEnableResult>;
  readonly onCheckpoint: (checkpoint: EndlessCheckpoint) => void;
  readonly onFinish: (result: MatchResult) => void;
  readonly onExit: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<MatchEngine | null>(null);
  const inputRef = useRef(new InputController());
  const rendererRef = useRef<CanvasRenderer | null>(null);
  const [snapshot, setSnapshot] = useState<MatchSnapshot | null>(null);
  const [paused, setPaused] = useState(false);
  const [boosting, setBoosting] = useState(false);
  const [touchControlsVisible, setTouchControlsVisible] = useState(
    () => window.matchMedia("(pointer: coarse)").matches,
  );
  const [assistError, setAssistError] = useState("");
  const [debugVisible, setDebugVisible] = useState(
    import.meta.env.DEV && new URLSearchParams(location.search).has("debug"),
  );
  const [frameMetrics, setFrameMetrics] = useState({ fps: 0, frameMs: 0 });
  const [engineGeneration, setEngineGeneration] = useState(0);
  const finishedRef = useRef(false);
  const pausedRef = useRef(false);
  const debugRef = useRef(debugVisible);
  const settingsRef = useRef(settings);
  const cosmeticRef = useRef(cosmetic);
  const audioRef = useRef(audio);
  const onFinishRef = useRef(onFinish);
  const onCheckpointRef = useRef(onCheckpoint);
  const checkpointEndlessRef = useRef<() => void>(() => undefined);
  const stopLoopRef = useRef<() => void>(() => undefined);
  const pauseMatchRef = useRef<() => void>(() => undefined);
  const resumeMatchRef = useRef<() => void>(() => undefined);
  const testTimeScaleRef = useRef(() => {
    if (!import.meta.env.DEV) return 1;
    const query = new URLSearchParams(location.search);
    if (query.has("visual-test")) return 0;
    const requestedScale = Number(query.get("test-clock-scale"));
    if (Number.isFinite(requestedScale) && requestedScale > 0) {
      return Math.min(60, requestedScale);
    }
    return query.has("test-clock") ? 60 : 1;
  });
  const resolvedTestTimeScaleRef = useRef(testTimeScaleRef.current());

  useEffect(() => {
    pausedRef.current = paused;
    const playerAlive =
      engineRef.current?.snapshot.snakes.find((snake) => snake.id === "player")
        ?.alive ?? true;
    inputRef.current.setEnabled(!paused && playerAlive && !finishedRef.current);
    if (paused || !playerAlive || finishedRef.current) {
      setBoosting(false);
    }
  }, [paused]);

  useEffect(() => {
    settingsRef.current = settings;
    cosmeticRef.current = cosmetic;
    debugRef.current = debugVisible;
    rendererRef.current?.resize({
      performance: settings.performance,
    });
  }, [cosmetic, debugVisible, settings]);

  useEffect(() => {
    audioRef.current = audio;
    onFinishRef.current = onFinish;
    onCheckpointRef.current = onCheckpoint;
  }, [audio, onCheckpoint, onFinish]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const testSeed = import.meta.env.DEV
      ? new URLSearchParams(location.search).get("test-seed")
      : null;
    const engine = MatchEngine.createDefault(
      testSeed ? `${testSeed}-${mode}` : `${Date.now()}-${crypto.randomUUID()}`,
      {
        mode,
        readySeconds:
          showTutorial || !settingsRef.current.skipReadyCountdown ? 3 : 1,
      },
    );
    setEngineGeneration((value) => value + 1);
    const renderer = new CanvasRenderer(canvas);
    const input = inputRef.current;
    engineRef.current = engine;
    rendererRef.current = renderer;
    input.attach(canvas);
    canvas.dataset.renderCount = "0";
    renderer.resize({
      performance: settingsRef.current.performance,
    });
    setSnapshot(engine.snapshot);

    let frame = 0;
    let last = performance.now();
    let hudAccumulator = 0;
    let previousPlayerFoodCount =
      engine.resultFor("player")?.foodCollected ?? 0;
    let previousTideCycle = -1;
    let previousBoosting = false;
    let previousPlayerAlive = true;
    let metricStartedAt = performance.now();
    let metricFrames = 0;
    let lastRenderedAt = last;
    let lastCheckpointSeconds = 0;
    let finishTimer: number | null = null;
    let running = true;
    let renderedFrameCount = 0;
    const framePacer = new FramePacer();
    const stopLoop = () => {
      if (finishTimer !== null) {
        window.clearTimeout(finishTimer);
        finishTimer = null;
      }
      if (!running) return;
      running = false;
      cancelAnimationFrame(frame);
    };
    stopLoopRef.current = stopLoop;
    const checkpointEndless = () => {
      if (mode !== "endless") return;
      const result = engine.resultFor("player");
      if (!result) return;
      onCheckpointRef.current({
        sessionMs: result.sessionMs ?? 0,
        aliveMs: result.aliveMs ?? 0,
        currentLength: Math.round(result.currentLength ?? 160),
        maxLength: Math.round(result.maxLength),
        eliminations: result.eliminations,
        deaths: result.deaths ?? 0,
        foodCollected: result.foodCollected,
      });
      lastCheckpointSeconds = engine.renderState.current.elapsedSeconds;
    };
    checkpointEndlessRef.current = checkpointEndless;
    const renderFrame = (now: number) => {
      if (!running) return;
      const delta = Math.min(0.1, Math.max(0, (now - last) / 1000));
      last = now;
      if (!pausedRef.current) {
        const intent = input.update(delta);
        if (intent.boosting && !previousBoosting) {
          audioRef.current.cue("boost", settingsRef.current);
          if (settingsRef.current.vibration && "vibrate" in navigator)
            navigator.vibrate(18);
        }
        previousBoosting = intent.boosting;
        engine.setIntent("player", intent);
        engine.advance(delta * resolvedTestTimeScaleRef.current);
      }
      const renderState = engine.renderState;
      const current = renderState.current;
      if (
        mode === "endless" &&
        current.elapsedSeconds - lastCheckpointSeconds >= 30
      ) {
        checkpointEndless();
      }
      const playerFoodCount = engine.resultFor("player")?.foodCollected ?? 0;
      if (playerFoodCount > previousPlayerFoodCount)
        audioRef.current.cue("pickup", settingsRef.current);
      previousPlayerFoodCount = playerFoodCount;
      const tideCycle = Math.floor(current.elapsedSeconds / 20);
      if (tideCycle > previousTideCycle) {
        if (previousTideCycle >= 0)
          audioRef.current.cue("tide", settingsRef.current);
        previousTideCycle = tideCycle;
      }
      const player = current.snakes.find((snake) => snake.id === "player");
      const playerAlive = player?.alive === true;
      if (previousPlayerAlive && !playerAlive) {
        input.setEnabled(false);
        engine.setIntent("player", { targetHeading: 0, boosting: false });
        setBoosting(false);
        previousBoosting = false;
      }
      if (
        mode === "endless" &&
        !previousPlayerAlive &&
        playerAlive &&
        !pausedRef.current
      ) {
        input.setEnabled(true);
        engine.setIntent("player", { targetHeading: 0, boosting: false });
        setBoosting(false);
        previousBoosting = false;
      }
      previousPlayerAlive = playerAlive;
      const terminal =
        current.phase === "ended" || (mode === "signal" && !playerAlive);
      const pacedRender = framePacer.shouldRender(
        now,
        settingsRef.current.performance,
      );
      if (pacedRender || terminal) {
        const rendered = interpolateMatchSnapshot(
          renderState.previous,
          renderState.current,
          renderState.interpolationAlpha,
        );
        renderer.render(rendered, engine.debugSnapshot, {
          ...settingsRef.current,
          debug: debugRef.current,
          equippedCosmetic: cosmeticRef.current,
          renderDeltaSeconds: Math.min(
            0.1,
            Math.max(0, (now - lastRenderedAt) / 1_000),
          ),
        });
        renderedFrameCount += 1;
        canvas.dataset.renderCount = String(renderedFrameCount);
        canvas.dataset.renderedPlayerAlive =
          rendered.snakes.find((snake) => snake.id === "player")?.alive === true
            ? "true"
            : "false";
        lastRenderedAt = now;
        metricFrames += 1;
      }
      const metricElapsed = now - metricStartedAt;
      if (metricElapsed >= 500) {
        setFrameMetrics({
          fps: Math.round((metricFrames * 1_000) / metricElapsed),
          frameMs:
            metricFrames === 0
              ? 0
              : Math.round((metricElapsed / metricFrames) * 10) / 10,
        });
        metricStartedAt = now;
        metricFrames = 0;
      }
      hudAccumulator += delta;
      if (hudAccumulator >= 0.1) {
        hudAccumulator = 0;
        setSnapshot(engine.snapshot);
      }

      if (!finishedRef.current && terminal) {
        finishedRef.current = true;
        input.setEnabled(false);
        setBoosting(false);
        previousBoosting = false;
        setSnapshot(engine.snapshot);
        running = false;
        finishTimer = window.setTimeout(
          () => onFinishRef.current(resultFromEngine(engine)),
          settingsRef.current.reducedMotion ? 0 : 520,
        );
        return;
      }
      frame = requestAnimationFrame(renderFrame);
    };
    frame = requestAnimationFrame(renderFrame);

    const onResize = () =>
      renderer.resize({
        performance: settingsRef.current.performance,
      });
    const pauseImmediately = () => {
      pausedRef.current = true;
      input.setEnabled(false);
      setBoosting(false);
      previousBoosting = false;
      setSnapshot(engine.snapshot);
      setPaused(true);
      last = performance.now();
      lastRenderedAt = last;
      framePacer.reset();
    };
    const resumeImmediately = () => {
      const playerAlive =
        engine.snapshot.snakes.find((snake) => snake.id === "player")?.alive ??
        true;
      pausedRef.current = false;
      input.setEnabled(playerAlive && !finishedRef.current);
      setBoosting(false);
      previousBoosting = false;
      setPaused(false);
      last = performance.now();
      lastRenderedAt = last;
      framePacer.reset();
    };
    pauseMatchRef.current = pauseImmediately;
    resumeMatchRef.current = resumeImmediately;
    const onVisibility = () => {
      if (document.hidden) {
        checkpointEndless();
        pauseImmediately();
      }
      last = performance.now();
      lastRenderedAt = last;
      framePacer.reset();
    };
    const onBlur = () => pauseImmediately();
    const onPageHide = () => {
      checkpointEndless();
      pauseImmediately();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.code === "Escape" || event.code === "KeyP") {
        // Escape must not also cancel the native dialog opened by this event.
        event.preventDefault();
        if (pausedRef.current) resumeImmediately();
        else pauseImmediately();
      }
      if (import.meta.env.DEV && event.code === "Backquote")
        setDebugVisible((value) => !value);
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("blur", onBlur);
    window.addEventListener("pagehide", onPageHide);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      stopLoop();
      pauseMatchRef.current = () => undefined;
      resumeMatchRef.current = () => undefined;
      checkpointEndlessRef.current = () => undefined;
      stopLoopRef.current = () => undefined;
      input.dispose();
      window.removeEventListener("resize", onResize);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("pagehide", onPageHide);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [mode]);

  useEffect(() => {
    if (!assistAllowed) engineRef.current?.setLeoAssist(false);
  }, [assistAllowed]);

  const player = snapshot?.snakes.find((snake) => snake.id === "player");
  const tutorial = !showTutorial
    ? ""
    : !snapshot || snapshot.elapsedSeconds < 6
      ? touchControlsVisible
        ? "拖动左侧航盘转向，靠近微光即可拾取"
        : "移动指针或按 WASD / 方向键转向"
      : snapshot.elapsedSeconds < 14
        ? "吃下微光，让线迹明显增长"
        : snapshot.elapsedSeconds < 24
          ? "按住 Space 或右侧按钮加速；长度会被消耗"
          : "";
  const latestDeath = snapshot?.deaths.at(-1);
  const playerProtection = player?.protectionRemainingSeconds ?? 0;
  const playerRespawn = player?.respawnRemainingSeconds ?? 0;

  const toggleAssist = async () => {
    const engine = engineRef.current;
    if (!engine) return;
    const enabling = snapshot?.assistEnabled !== true;
    if (enabling) {
      const authorization = await onEnableAssist();
      if (!authorization.ok) {
        setAssistError(
          authorization.message ?? "无法安全标记本局，LEO ASSIST 保持关闭",
        );
        return;
      }
    }
    setAssistError("");
    engine.setLeoAssist(enabling);
  };

  const exitCurrentMatch = () => {
    const engine = engineRef.current;
    if (mode !== "endless" || !engine) {
      onExit();
      return;
    }
    finishedRef.current = true;
    pausedRef.current = true;
    inputRef.current.setEnabled(false);
    setBoosting(false);
    checkpointEndlessRef.current();
    stopLoopRef.current();
    onFinishRef.current(resultFromEngine(engine, "exit"));
  };

  const aimFromJoystick = (event: React.PointerEvent<HTMLDivElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    inputRef.current.aim(
      Math.atan2(
        event.clientY - bounds.top - bounds.height / 2,
        event.clientX - bounds.left - bounds.width / 2,
      ),
    );
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  return (
    <main
      className="game-stage"
      data-performance={settings.performance}
      data-touch-controls={touchControlsVisible ? "visible" : "auto"}
      data-mode={snapshot?.mode ?? mode}
      data-assisted={snapshot?.assisted ? "true" : "false"}
      inert={resultVisible}
      onPointerDownCapture={(event) => {
        if (event.pointerType === "touch") setTouchControlsVisible(true);
      }}
    >
      <canvas
        ref={canvasRef}
        className="game-canvas"
        aria-label="Gloamline 对局场地"
        data-player-boosting={player?.boosting ? "true" : "false"}
        data-input-enabled={inputRef.current.isEnabled ? "true" : "false"}
        data-player-target-heading={player?.targetHeading.toFixed(6) ?? "0"}
        data-elapsed-seconds={snapshot?.elapsedSeconds.toFixed(3) ?? "0.000"}
        data-engine-generation={engineGeneration}
        data-render-fps={frameMetrics.fps}
        data-simulation-step={
          engineRef.current?.debugSnapshot.simulatedStep ?? 0
        }
        data-ready-seconds={(snapshot?.readyRemainingSeconds ?? 0).toFixed(3)}
        data-respawn-seconds={playerRespawn.toFixed(3)}
        data-protection-seconds={playerProtection.toFixed(3)}
        data-food-count={snapshot?.foods.length ?? 0}
        data-death-count={snapshot?.deaths.length ?? 0}
        data-trail-points={
          snapshot?.snakes.reduce(
            (total, snake) => total + snake.trail.points.length,
            0,
          ) ?? 0
        }
        data-theme={settings.theme}
      />
      <div className="hud">
        <div className="hud-top">
          <div className="hud-cluster">
            <div className="hud-cell">
              <small>{mode === "endless" ? "航程" : "时间"}</small>
              <strong>
                {mode === "endless"
                  ? Math.floor(snapshot?.elapsedSeconds ?? 0)
                  : Math.ceil(snapshot?.remainingSeconds ?? 180)}
                s
              </strong>
            </div>
            <div className="hud-cell hud-mode">
              <small>{mode === "endless" ? "无尽漫游" : "逐光竞技"}</small>
              <strong>{mode === "endless" ? "ENDLESS" : "SIGNAL"}</strong>
            </div>
            <div className="hud-cell">
              <small>长度</small>
              <strong>{Math.round(player?.length ?? 160)}</strong>
            </div>
          </div>
          <button
            className="ghost-button hud-action"
            disabled={resultVisible}
            onClick={() => pauseMatchRef.current()}
          >
            <Icon name="pause" />
            暂停
          </button>
        </div>
        <Leaderboard rankings={snapshot?.rankings ?? []} />
        {playerRespawn > 0 ? (
          <div className="match-state-toast" role="status">
            重新加入 · {Math.max(1, Math.ceil(playerRespawn))}s
          </div>
        ) : playerProtection > 0 ? (
          <div className="match-state-toast" role="status">
            中性防护 · {Math.max(1, Math.ceil(playerProtection))}s
          </div>
        ) : null}
        {snapshot?.assisted ? (
          <div className="assist-status" role="status">
            LEO ASSIST · 本局不计正式进度
          </div>
        ) : null}
        {assistAllowed ? (
          <button
            className="assist-button"
            type="button"
            aria-pressed={snapshot?.assistEnabled === true}
            onClick={() => void toggleAssist()}
          >
            {snapshot?.assistEnabled ? "关闭 LEO ASSIST" : "开启 LEO ASSIST"}
          </button>
        ) : null}
        {assistError ? (
          <div className="assist-error" role="alert">
            {assistError}
          </div>
        ) : null}
        {snapshot?.phase === "ready" && !paused && !resultVisible ? (
          <div className="ready-overlay" role="status" aria-live="polite">
            <small>航迹准备</small>
            <strong>
              {Math.max(1, Math.ceil(snapshot.readyRemainingSeconds ?? 0))}
            </strong>
            <span>先选好方向，跟着微光出发。</span>
          </div>
        ) : null}
        {tutorial &&
        snapshot?.phase !== "ready" &&
        !paused &&
        !resultVisible ? (
          <div className="tutorial-toast" aria-live="polite">
            {tutorial}
          </div>
        ) : null}
        {snapshot?.phase === "playing" &&
        snapshot.elapsedSeconds % 20 < 2 &&
        playerRespawn <= 0 &&
        playerProtection <= 0 ? (
          <div className="event-toast">
            <Icon name="spark" />
            共振潮汐 · 微光价值提升
          </div>
        ) : null}
        <div
          className="joystick"
          aria-label="触屏转向航盘"
          onPointerDown={aimFromJoystick}
          onPointerMove={(event) => event.buttons > 0 && aimFromJoystick(event)}
        />
        <button
          className="boost-button"
          data-active={boosting}
          aria-pressed={boosting}
          disabled={
            paused ||
            resultVisible ||
            player?.alive !== true ||
            snapshot?.phase === "ready"
          }
          onPointerDown={(event) => {
            event.currentTarget.setPointerCapture(event.pointerId);
            inputRef.current.boost(true);
            setBoosting(true);
          }}
          onPointerUp={() => {
            inputRef.current.boost(false);
            setBoosting(false);
          }}
          onPointerCancel={() => {
            inputRef.current.boost(false);
            setBoosting(false);
          }}
        >
          <span className="boost-primary">加速</span>
          <span className="boost-detail">消耗长度</span>
          <span className="boost-key-hint">Space / Shift</span>
        </button>
      </div>
      {debugVisible ? (
        <output className="debug-panel" aria-label="碰撞与性能调试指标">
          <span>FPS {frameMetrics.fps}</span>
          <span>Frame {frameMetrics.frameMs.toFixed(1)}ms</span>
          <span>
            Step {engineRef.current?.debugSnapshot.simulatedStep ?? 0}
          </span>
          <span>
            Buckets {engineRef.current?.debugSnapshot.spatialBucketCount ?? 0}
          </span>
          <span>
            Spawn boundary{" "}
            {Math.round(
              engineRef.current?.debugSnapshot.spawn?.boundaryDistance ?? 0,
            )}
          </span>
          <span>
            Nearest head/body{" "}
            {Math.round(
              engineRef.current?.debugSnapshot.spawn?.nearestDangerousHead
                ?.distance ?? 0,
            )}
            /
            {Math.round(
              engineRef.current?.debugSnapshot.spawn?.nearestDangerousBody
                ?.distance ?? 0,
            )}
          </span>
          <span>
            Death{" "}
            {latestDeath
              ? `${latestDeath.snakeId}:${latestDeath.reason}←${latestDeath.sourceSnakeId ?? "arena"}`
              : "none"}
          </span>
        </output>
      ) : null}
      {paused && !resultVisible ? (
        <Dialog
          labelledBy="pause-title"
          onDismiss={() => resumeMatchRef.current()}
          className="pause-dialog"
        >
          <div className="result-emblem">
            <Icon name="pause" />
          </div>
          <p className="dialog-kicker">TAKE A BREATH</p>
          <h2 id="pause-title">航迹暂停</h2>
          <p className="dialog-copy">
            微光会在这里等你。切换页面时自动暂停，回来即可继续。
          </p>
          <div className="dialog-actions">
            <button
              className="primary-button"
              onClick={() => resumeMatchRef.current()}
            >
              <Icon name="play" />
              继续
              <Icon name="arrow" />
            </button>
            <button className="secondary-button" onClick={exitCurrentMatch}>
              离开本局
            </button>
            {import.meta.env.DEV ? (
              <button
                className="secondary-button"
                onClick={() => setDebugVisible((value) => !value)}
              >
                碰撞调试
              </button>
            ) : null}
          </div>
        </Dialog>
      ) : null}
    </main>
  );
}
